/* eslint-disable no-case-declarations */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
// Store
import { Store } from '@ngxs/store';
import { SetIntegrationBackUrl, SetIntegrationOrganization, SetIntegrationOrganizationMenu } from '../../state/actions/generic-integation.actions';
// Service
import { BroadcastEventModel, BroadcastService, ObjectUtils, SystemApiService } from '@cv-invision/core';
// Models
import { IntegrationsUrlParamsModel } from '../../models/integrations-url-params.model';
import {
	IntegrationDocumentTypeColumns,
	IntegrationMappersColumns,
	IntegrationOrganizationColumns,
	IntegrationProductTypeColumns,
	IntegrationInboudOrderStatusColumns,
	IntegrationOnHoldClientAccessColumns,
	IntegrationMessagesToIgnoreColumns,
	IntegrationOnholdClientAccessColumns
} from '../../models/integration-columns.model';
import { EditableTableActions } from '../common/editable-table/model/editable-table.model';
import { IdNameObjectType } from "../../../state/models/global-configuration-state.model";
import { GlobalConfigurationState } from "../../../state/global-configuration.state";

@Component({
	selector: 'cv-invision-ux-table-like',
	templateUrl: './table-like.component.html',
	styleUrls: ['./table-like.component.scss']
})
export class TableLikeComponent implements OnInit, OnDestroy {

	params = new IntegrationsUrlParamsModel();
	model: any = null;
	label = '';
	extraData: any = {};
	public formBuilderConfig: any = null;

	private atComponentDestroy: Subject<any> = new Subject<any>();

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private store: Store,
		private broadcast: BroadcastService,
		private systemApiService: SystemApiService
	) { }

	ngOnInit(): void {
		const trackingSystemList: Array<IdNameObjectType> = this.store.selectSnapshot(GlobalConfigurationState.getTrackingSystems);

		this.route.params
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(params => {
				this.params = new IntegrationsUrlParamsModel();
				this.model = null;
				this.formBuilderConfig = null;
				this.extraData = {
					editInModal: true
				};
				this.params = Object.assign(this.params, params);
				let backUrl = '/global-configuration/generic-integrations';

				if ('mapperType' in params) {
					this.params.isMapper = true;
					this.params.section = params.mapperType;
				}

				switch (this.params.section) {
					case 'product-types':
						this.model = IntegrationProductTypeColumns;
						this.label = 'Product Types';
						break;
					case 'document-types':
						this.model = IntegrationDocumentTypeColumns;
						this.label = 'Document Types';

						const documentTypesTrackingSystemList = ObjectUtils.recursiveDeepCopy(trackingSystemList);
						documentTypesTrackingSystemList.push({ name: 'ClassValuation', id: 'ClassValuation' });
						this.loadTrackingSystemOptions(documentTypesTrackingSystemList, 'documentTypes');

						break;
					case 'organizations':
						this.model = IntegrationOrganizationColumns;
						this.label = 'Organizations';
						this.extraData = Object.assign(this.extraData, { showSelect: true, editInModal: true, saveAsObject: true });
						this.extraData.rootUrl = this.systemApiService.api.identitySystemService;
						break;
					case 'status-messages':
						this.model = IntegrationInboudOrderStatusColumns;
						this.label = 'Inbound Order Status';
						this.loadStatusMessagesOptions();
						break;
					case 'onhold-client-access':
						this.model = IntegrationOnHoldClientAccessColumns;
						this.label = 'On Hold Client Access';
						this.loadTrackingSystemList();
						this.loadCMPOnHoldReasonsOptions();
						this.loadReceiveFromClientOptions();
						break;
					case 'messages-to-ignore':
						this.model = IntegrationMessagesToIgnoreColumns;
						this.label = 'Outbound Messages';
						this.loadTrackingSystemList();
						break;
					case 'inbound-messages':
						this.model = IntegrationOnholdClientAccessColumns;
						this.label = 'Inbound Messages';
						this.loadTrackingSystemList();
						this.loadHandleModeList();
						break;
				}

				if (this.params.isMapper) {
					this.model = { ...this.model, ...IntegrationMappersColumns };
					this.formBuilderConfig = { ...IntegrationMappersColumns.formBuilderConfig };
					this.label = this.params.section;
					const type = this.params.section.charAt(0).toLocaleLowerCase() + this.params.section.slice(1);

					if (this.params.section === 'OnHoldReason' || this.params.section === 'OrderStatus') {
						this.model.model[4] = { ...this.model.model[4], ...{ disabled: false } };
						this.model.model[5] = { ...this.model.model[5], ...{ disabled: false } };

						this.model.model = this.model.model.filter((model: any) => model.label != 'Tracking System Option');

						this.formBuilderConfig.trackingSystemId = {
							...this.formBuilderConfig.trackingSystemId,
							... {
								DataType: 'String',
								DisplayText: 'Tracking System Id',
								UIEvents: {}
							}
						};
						this.formBuilderConfig.trackingSystemLabel = { ...this.formBuilderConfig.trackingSystemLabel, ... { hidden: false } };
					}

					const documentTypesTrackingSystemList = ObjectUtils.recursiveDeepCopy(trackingSystemList);
					this.loadTrackingSystemOptions(documentTypesTrackingSystemList, type);
				} else {
					this.formBuilderConfig = { ...this.model.formBuilderConfig };
				}

				if ('organizationIntegrationId' in params) {
					backUrl = `/global-configuration/generic-integration/${params.integrationId}/organizations`;
					this.params.parentIntegrationId = this.params.integrationId;
					this.params.integrationId = params.organizationIntegrationId;
				}

				this.store.dispatch(new SetIntegrationBackUrl(backUrl));
			});

		this.broadcast.onEvent(EditableTableActions.TABLE_ROW_ACTION)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(async (e: BroadcastEventModel) => {
				const row = e.event.row;
				let url = `/global-configuration/generic-integration/${this.params.integrationId}`;

				switch (e.event.type) {
					case 'goTo':
						this.store.dispatch(new SetIntegrationBackUrl(`${url}/organizations`));
						url = `${url}/organization/${row.integrationId}/product-types`;

						await this.store.dispatch(new SetIntegrationOrganization(row)).toPromise();
						await this.store.dispatch(new SetIntegrationOrganizationMenu()).toPromise();
						break;
				}

				this.router.navigate([url]);
			});
	}

	loadTrackingSystemOptions(trackingSystemList: IdNameObjectType[], mapperType?: string): void {
		if (trackingSystemList.length > 0) {
			this.model['formBuilderConfig']['trackingSystem']['options'] = trackingSystemList?.map((item: IdNameObjectType) => {
				return {
					label: item.name,
					value: item.id,
				};
			});

			if (this.params.section !== 'OnHoldReason' && this.params.section !== 'OrderStatus'){
				trackingSystemList.forEach((trackingSystem: IdNameObjectType) => {
					const trackingSystemStoreName = trackingSystem.id[0].toLowerCase() + trackingSystem.id.substring(1);
					const mapperTypesList = this.store.selectSnapshot((state: any) => ObjectUtils.getStateFromString(state, `globalConfiguration.${trackingSystemStoreName}.${mapperType}`));
					const mapperTypesOptions = mapperTypesList.map((item: any) => {
						return {
							label: item.name,
							value: item.id,
						};
					});

					const productsData = {}
					productsData[trackingSystemStoreName] = mapperTypesOptions;

					this.extraData = Object.assign(this.extraData, productsData);
				})
			}

		}
	}

	/**
	 * Load status messages options
	 */
	private loadStatusMessagesOptions(): void {
		const actionList: Array<IdNameObjectType> = this.store.selectSnapshot(GlobalConfigurationState.getActionOptions);

		if (actionList.length > 0) {
			this.model['formBuilderConfig']['action']['options'] = actionList?.map((item: IdNameObjectType) => {
				return {
					label: item.name,
					value: item.id,
				};
			});
		}
	}

	/**
	 * Load CMP On Hold Reasons options
	 */
	private loadCMPOnHoldReasonsOptions(): void {
		const CMPOnHoldReasonsList: Array<IdNameObjectType> = this.store.selectSnapshot(GlobalConfigurationState.getCMPOnHoldReasonsOptions);

		if (CMPOnHoldReasonsList.length > 0) {
			this.model['formBuilderConfig']['holdReasonId']['options'] = CMPOnHoldReasonsList?.map((item: IdNameObjectType) => {
				return {
					label: item.name,
					value: item.id,
				};
			});
		}
	}

	/**
	 * Load receive from client options
	 */
	private loadReceiveFromClientOptions(): void {
		const receiveFromClientList: Array<IdNameObjectType> = this.store.selectSnapshot(GlobalConfigurationState.getReceiveFromClientOptions);

		if (receiveFromClientList.length > 0) {
			this.model['formBuilderConfig']['receiveFromClient']['options'] = receiveFromClientList?.map((item: IdNameObjectType) => {
				return {
					label: item.name,
					value: item.id,
				};
			});
		}
	}

	/**
	 * Load the tracking system list from client options
	 */
	private loadTrackingSystemList(): void {
		const trackingSystemList: Array<IdNameObjectType> = this.store.selectSnapshot(GlobalConfigurationState.getTrackingSystems);
		this.model['formBuilderConfig']['trackingSystem']['options'] = trackingSystemList.map((item: IdNameObjectType) => {
			return {
				label: item.name,
				value: item.id,
			};
		});
	}

	/**
	 * Load the handle mode list from client options
	 */
	private loadHandleModeList(): void {
		const handleModeList: Array<string> = this.store.selectSnapshot(GlobalConfigurationState.gethandleMode);
		this.model['formBuilderConfig']['handleMode']['options'] = handleModeList.map((item: string) => {
			return {
				label: item,
				value: item,
			};
		});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next(true);
		this.atComponentDestroy.unsubscribe();
	}

}
